import { Grid } from "@mui/material";

import {
  CommunityNamesSelect,
  HomeHudNumberInput,
  HomeLocationSelect,
  HomeSizeInput,
  MakeCustomInput,
  ManufacturerSelect,
  ModelHomeInput,
  RoofConstructionYearSelect,
  RoofShapesSelect,
  SkirtingTypeSelect,
} from "@covertree/new-business-forms";
import {
  FormHomeTypeSelect,
  FormModelYearAutocomplete,
  FormSectionHeading,
} from "@covertree/ui";
import { theme } from "@covertree/theme";

import { SectionCard } from "../../../SectionCard";
import { useHomeDetails } from "./useHomeDetails";

const HOME_DETAILS_FIELDS = [
  "homeType",
  "manufacturer",
  "makeCustom",
  "modelHome",
  "modelYear",
  "totalSquareFootage",
  "homeWidth",
  "homeLength",
  "location",
  "communityName",
  "roofShape",
  "roofYearYyyy",
  "homeHudNumber",
  "skirtingType",
] as const;

interface HomeDetailsProps {
  viewOnly?: boolean;
}

/** Home Details section — home, dimension, and construction fields. */
export const HomeDetails = ({ viewOnly = false }: HomeDetailsProps) => {
  const {
    control,
    errors,
    stateCode,
    isTenantFlow,
    showMakeCustom,
    formModelYearValue,
    showParkNameSection,
    showHomeHudAndSkirtingType,
  } = useHomeDetails();

  return (
    <SectionCard
      title="Home Details"
      dataTestId="home-details-accordion"
      fieldNames={[...HOME_DETAILS_FIELDS]}>
      <Grid container spacing={1.5}>
        {!isTenantFlow && (
          <>
            <FormSectionHeading
              title="Home Information"
              showDivider={false}
              fontSize={18}
            />
            <Grid item xs={12} sm={6}>
              {/* disabled for MVP */}
              <ManufacturerSelect disabled fontSize={18} labelFontSize={16} />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* disabled for MVP */}
              <FormHomeTypeSelect
                control={control}
                fontSize={18}
                labelFontSize={16}
                hasUnknown={false}
                disabled
              />
            </Grid>
            {showMakeCustom && (
              <Grid item xs={12} mt={errors?.homeType ? 3 : undefined}>
                {/* disabled for MVP */}
                <MakeCustomInput disabled fontSize={18} labelFontSize={16} />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <ModelHomeInput
                disabled={viewOnly}
                fontSize={18}
                labelFontSize={16}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* disabled for MVP */}
              <FormModelYearAutocomplete
                inputStyle={{
                  padding: "0px !important",
                }}
                control={control}
                fontSize={18}
                labelFontSize={16}
                disabled
              />
            </Grid>
            <FormSectionHeading title="Home Dimensions" fontSize={18} />
            <Grid item xs={12} mt={theme.spacing(3)}>
              {/* disabled for MVP */}
              <HomeSizeInput
                disabled
                fontSize={18}
                labelFontSize={16}
                shouldReValidateOnModeChange={false}
              />
            </Grid>
            <FormSectionHeading title="Location & Property" fontSize={18} />
          </>
        )}
        <Grid item xs={12}>
          <HomeLocationSelect
            disabled={viewOnly}
            fontSize={18}
            labelFontSize={16}
            isMyOwnLand={!isTenantFlow}
          />
        </Grid>
        {showParkNameSection && (
          <Grid item xs={12}>
            <CommunityNamesSelect
              stateCode={stateCode}
              disabled={viewOnly}
              fontSize={18}
              labelFontSize={16}
            />
          </Grid>
        )}
        {isTenantFlow && (
          <Grid item xs={12}>
            {/* disabled for MVP */}
            <FormHomeTypeSelect
              control={control}
              fontSize={18}
              labelFontSize={16}
              hasUnknown={false}
              disabled
            />
          </Grid>
        )}
        {!isTenantFlow && (
          <>
            <FormSectionHeading title="Construction Details" fontSize={18} />
            <Grid item xs={12} sm={6}>
              <RoofShapesSelect
                disabled={viewOnly}
                fontSize={18}
                labelFontSize={16}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RoofConstructionYearSelect
                minYear={formModelYearValue}
                disabled={viewOnly}
                fontSize={18}
                labelFontSize={16}
              />
            </Grid>
            {showHomeHudAndSkirtingType && (
              <>
                <Grid item xs={12} sm={6}>
                  <HomeHudNumberInput
                    disabled={viewOnly}
                    fontSize={18}
                    labelFontSize={16}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SkirtingTypeSelect
                    disabled={viewOnly}
                    fontSize={18}
                    labelFontSize={16}
                  />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </SectionCard>
  );
};
